import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';

const GoalTargets = () => {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [changes, setChanges] = useState({});
  const [editingValues, setEditingValues] = useState({});
  const [savingCells, setSavingCells] = useState({});
  const [savedCells, setSavedCells] = useState({});
  const [showForecastModal, setShowForecastModal] = useState(false);
  const [showSummaryModal, setShowSummaryModal] = useState(false);
  const [showSummaryInMatrix, setShowSummaryInMatrix] = useState(false);
  const [summaryConfig, setSummaryConfig] = useState({
    showQuarterly: true,
    showSemesterly: true,
    showAnnually: true
  });
  const [forecastConfig, setForecastConfig] = useState({
    targetYear: null,
    growthMode: 'percent', // 'percent' ou 'absolute'
    growthPercent: 10,
    growthAbsolute: 0,
    growthType: 'continuous', // 'continuous', 'seasonal', 'both'
    hasMinValue: false,
    minValue: 0,
    hasMaxValue: false,
    maxValue: 100
  });
  
  const saveTimeoutRef = useRef(null);
  const pendingChangesRef = useRef({});

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`/goals/${id}/targets`);
      if (response.data.success) {
        setData(response.data.data);
      }
    } catch (err) {
      console.error('Error loading targets:', err);
      setError('Erro ao carregar matriz de targets');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Formatar número para exibição
  const formatNumber = (value) => {
    if (value === null || value === undefined || value === '' || (typeof value === 'number' && isNaN(value))) {
      return '';
    }
    return new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value);
  };

  // Salvar alteração
  const saveChange = async (changeData, cellKey) => {
    try {
      setSavingCells(prev => ({ ...prev, [cellKey]: true }));
      
      const response = await axios.post('/matrix/update', {
        matrix_data: [changeData]
      });

      if (response.data.success) {
        // Atualizar dados localmente sem recarregar
        setData(prev => {
          if (!prev || !prev.matrix) return prev;
          
          return {
            ...prev,
            matrix: prev.matrix.map(row => ({
              ...row,
              cells: row.cells.map(cell => {
                if (cell.date === changeData.date) {
                  const updatedCell = { ...cell };
                  
                  if (changeData.target !== undefined) {
                    updatedCell.target = changeData.target;
                  }
                  
                  if (changeData.result !== undefined) {
                    if (changeData.result === null) {
                      updatedCell.result = null;
                    } else {
                      updatedCell.result = changeData.result;
                    }
                  }
                  
                  // Sempre recalcular percentual com valores atualizados
                  const targetVal = updatedCell.target || updatedCell.goal_target;
                  if (targetVal > 0 && updatedCell.result !== null && updatedCell.result !== undefined) {
                    updatedCell.percentage = Math.round((updatedCell.result / targetVal) * 1000) / 10;
                  } else {
                    updatedCell.percentage = null;
                  }
                  
                  return updatedCell;
                }
                return cell;
              })
            }))
          };
        });
        
        setSavedCells(prev => ({ ...prev, [cellKey]: true }));
        
        setTimeout(() => {
          setSavedCells(prev => {
            const newState = { ...prev };
            delete newState[cellKey];
            return newState;
          });
        }, 2000);
      }
    } catch (err) {
      console.error('Error saving:', err);
    } finally {
      setSavingCells(prev => {
        const newState = { ...prev };
        delete newState[cellKey];
        return newState;
      });
    }
  };

  // Salvar todas as alterações pendentes manualmente
  const handleSaveAll = async () => {
    const allPending = { ...pendingChangesRef.current };
    const keys = Object.keys(allPending);
    if (keys.length === 0) return;

    // Cancelar debounce pendente
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);

    for (const key of keys) {
      const changeToSave = allPending[key];
      if (changeToSave && (changeToSave.target !== undefined || changeToSave.result !== undefined)) {
        await saveChange(changeToSave, key);
        delete pendingChangesRef.current[key];
      }
    }
    setChanges({});
    setEditingValues({});
    
    // Recarregar dados do servidor para garantir consistência
    await loadData();
  };

  const hasChanges = Object.keys(changes).length > 0;

  // Handle input change
  const handleInputChange = (date, field, value, goalId = null) => {
    const editKey = `${date}_${field}`;
    setEditingValues(prev => ({ ...prev, [editKey]: value }));
  };

  // Handle target change with debounce
  const handleTargetChange = (date, field, value, goalId = null) => {
    const key = date;
    
    let parsedValue = null;
    if (value && value.trim() !== '') {
      const parsed = parseFloat(value.replace(/\./g, '').replace(',', '.'));
      parsedValue = isNaN(parsed) ? null : parsed;
    }
    
    const existing = pendingChangesRef.current[key] || { 
      parent_goal_id: data.parent_goal_id, 
      date 
    };
    const updated = { ...existing, [field]: parsedValue };
    if (goalId) updated.goal_id = goalId;
    
    pendingChangesRef.current[key] = updated;
    setChanges(prev => ({ ...prev, [key]: updated }));
    
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    
    saveTimeoutRef.current = setTimeout(() => {
      const changeToSave = pendingChangesRef.current[key];
      if (changeToSave && (changeToSave.target !== undefined || changeToSave.result !== undefined)) {
        saveChange(changeToSave, key);
        delete pendingChangesRef.current[key];
        setChanges(prev => {
          const newChanges = { ...prev };
          delete newChanges[key];
          return newChanges;
        });
        setEditingValues(prev => {
          const newState = { ...prev };
          delete newState[`${key}_target`];
          delete newState[`${key}_result`];
          return newState;
        });
      }
    }, 2000);
  };

  // Cleanup timeout
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, []);

  // Obter anos com resultados concluídos (usado como base para previsão)
  const getYearsWithResults = () => {
    if (!data || !data.matrix) return [];
    return data.years.filter(year => {
      return data.matrix.some(row => 
        row.cells.some(cell => 
          cell.year === year && 
          cell.result !== null && 
          cell.result !== undefined &&
          cell.status === 'concluded'
        )
      );
    });
  };

  // Abrir modal de previsão
  const openForecastModal = () => {
    if (!data) return;
    
    // Encontrar anos sem resultados (candidatos a destino)
    const yearsWithResults = getYearsWithResults();
    const availableTargetYears = data.years.filter(y => !yearsWithResults.includes(y));
    
    // Preferir o ano atual se disponível, senão o primeiro disponível
    let defaultTargetYear = availableTargetYears.includes(data.current_year) 
      ? data.current_year 
      : availableTargetYears[0] || data.current_year;
    
    // Calcular total base para pré-preencher o valor absoluto
    let totalBase = 0;
    let monthsCount = 0;
    data.matrix.forEach(row => {
      yearsWithResults.forEach(year => {
        const cell = row.cells.find(c => c.year === year);
        if (cell && cell.result !== null && cell.result !== undefined && cell.status === 'concluded') {
          totalBase += cell.result;
          monthsCount++;
        }
      });
    });
    const avgMonthBase = monthsCount > 0 ? totalBase / monthsCount : 0;
    const avgYearTotal = avgMonthBase * 12;
    
    setForecastConfig(prev => ({
      ...prev,
      targetYear: defaultTargetYear,
      growthAbsolute: Math.round(avgYearTotal * 100) / 100 // Inicializa com o valor base
    }));
    setShowForecastModal(true);
  };

  // Função para aplicar limites min/max
  const applyLimits = (value) => {
    let result = value;
    if (forecastConfig.hasMinValue && result < forecastConfig.minValue) {
      result = forecastConfig.minValue;
    }
    if (forecastConfig.hasMaxValue && result > forecastConfig.maxValue) {
      result = forecastConfig.maxValue;
    }
    return result;
  };

  // Executar previsão
  const executeForecast = async () => {
    const { targetYear, growthPercent, growthType } = forecastConfig;
    
    // Obter anos com resultados concluídos (todos são usados como base)
    const yearsWithResults = getYearsWithResults();
    
    if (yearsWithResults.length === 0) {
      alert('Nenhum ano com resultados concluídos encontrado para usar como base.');
      return;
    }
    
    if (!targetYear) {
      alert('Selecione o ano destino');
      return;
    }
    
    if (yearsWithResults.includes(targetYear)) {
      alert('O ano destino não pode ser um ano com resultados já concluídos');
      return;
    }
    
    // Coletar valores de TODOS os anos com resultados por mês
    // Para cada mês, calcular a média dos valores históricos
    const monthlyBaseValues = {}; // mês -> array de valores
    let countHistorico = 0;
    let lastValue = null;
    
    data.matrix.forEach((row, monthIdx) => {
      const monthNum = monthIdx + 1;
      monthlyBaseValues[monthNum] = [];
      
      yearsWithResults.forEach(year => {
        const cell = row.cells.find(c => c.year === year);
        if (cell && cell.result !== null && cell.result !== undefined && cell.status === 'concluded') {
          monthlyBaseValues[monthNum].push(cell.result);
          countHistorico++;
          lastValue = cell.result;
        }
      });
    });
    
    if (countHistorico === 0) {
      alert('Nenhum resultado concluído encontrado. Preencha ao menos alguns períodos.');
      return;
    }
    
    // Calcular média por mês (média dos valores históricos de cada mês)
    const avgMonthlyValues = {};
    let totalFromMonths = 0;
    let monthsWithData = 0;
    
    Object.keys(monthlyBaseValues).forEach(month => {
      const values = monthlyBaseValues[month];
      if (values.length > 0) {
        avgMonthlyValues[month] = values.reduce((a, b) => a + b, 0) / values.length;
        totalFromMonths += avgMonthlyValues[month];
        monthsWithData++;
      }
    });
    
    // Calcular média mensal real e extrapolar para 12 meses
    const avgMonthValue = monthsWithData > 0 ? totalFromMonths / monthsWithData : 0;
    const avgYearTotal = avgMonthValue * 12; // Extrapolado para ano completo
    
    // Calcular total destino baseado no modo (percentual ou absoluto)
    const isAbsoluteMode = forecastConfig.growthMode === 'absolute';
    let targetYearTotal;
    let growthFactor; // Fator multiplicativo para ambos os modos
    
    if (isAbsoluteMode) {
      // Modo absoluto: o usuário define o total alvo diretamente
      targetYearTotal = forecastConfig.growthAbsolute;
      growthFactor = avgYearTotal > 0 ? targetYearTotal / avgYearTotal : 1;
    } else {
      // Modo percentual: aplica o crescimento/redução sobre a base
      growthFactor = 1 + growthPercent / 100;
      targetYearTotal = avgYearTotal * growthFactor;
    }
    
    console.log(`[Forecast] Modo: ${forecastConfig.growthMode}, Meses com dados: ${monthsWithData}, Média mensal: ${avgMonthValue.toFixed(2)}, Total anual extrapolado: ${avgYearTotal.toFixed(2)}, Total destino: ${targetYearTotal.toFixed(2)}`);
    
    // Preparar mudanças a salvar
    const changesToSave = [];
    
    if (growthType === 'continuous') {
      // Crescimento contínuo uniforme
      // Janeiro começa do último mês conhecido com crescimento aplicado
      const avgMonthValue = avgYearTotal / 12;
      const januaryValue = (lastValue || avgMonthValue) * growthFactor;
      
      // Calcular incremento mensal usando progressão aritmética
      const n = 12;
      const d = (targetYearTotal - n * januaryValue) / (n * (n - 1) / 2);
      
      data.matrix.forEach((row, monthIdx) => {
        const targetCell = row.cells.find(c => c.year === targetYear);
        if (targetCell) {
          const newTarget = Math.max(0, januaryValue + (monthIdx * d));
          changesToSave.push({
            parent_goal_id: data.parent_goal_id,
            date: targetCell.date,
            target: Math.round(applyLimits(newTarget) * 100) / 100
          });
        }
      });
    } else if (growthType === 'seasonal') {
      // Crescimento sazonal - baseado na média do mesmo mês dos anos anteriores
      // Para meses sem dados históricos, usa a média geral
      data.matrix.forEach((row, monthIdx) => {
        const monthNum = monthIdx + 1;
        const avgBaseValue = avgMonthlyValues[monthNum] || avgMonthValue; // Usa média geral se não tiver dado do mês
        const targetCell = row.cells.find(c => c.year === targetYear);
        
        if (targetCell) {
          const newTarget = avgBaseValue * growthFactor;
          changesToSave.push({
            parent_goal_id: data.parent_goal_id,
            date: targetCell.date,
            target: Math.round(applyLimits(newTarget) * 100) / 100
          });
        }
      });
    } else if (growthType === 'both') {
      // Misto: combina crescimento contínuo e sazonal
      // Peso: 50% contínuo + 50% sazonal
      const avgMonthValue = avgYearTotal / 12;
      const januaryValue = (lastValue || avgMonthValue) * growthFactor;
      const n = 12;
      const d = (targetYearTotal - n * januaryValue) / (n * (n - 1) / 2);
      
      data.matrix.forEach((row, monthIdx) => {
        const monthNum = monthIdx + 1;
        const avgBaseValue = avgMonthlyValues[monthNum];
        const targetCell = row.cells.find(c => c.year === targetYear);
        
        if (targetCell) {
          const continuousValue = Math.max(0, januaryValue + (monthIdx * d));
          const seasonalValue = avgBaseValue 
            ? avgBaseValue * growthFactor
            : continuousValue;
          
          // Média ponderada: 50% contínuo + 50% sazonal
          const newTarget = (continuousValue + seasonalValue) / 2;
          changesToSave.push({
            parent_goal_id: data.parent_goal_id,
            date: targetCell.date,
            target: Math.round(applyLimits(newTarget) * 100) / 100
          });
        }
      });
    }
    
    // Salvar todas as mudanças
    try {
      const response = await axios.post('/matrix/update', {
        matrix_data: changesToSave
      });
      
      if (response.data.success) {
        // Atualizar dados localmente
        setData(prev => {
          if (!prev) return prev;
          return {
            ...prev,
            matrix: prev.matrix.map(row => ({
              ...row,
              cells: row.cells.map(cell => {
                const change = changesToSave.find(c => c.date === cell.date);
                if (change) {
                  return { ...cell, target: change.target };
                }
                return cell;
              })
            }))
          };
        });
        
        setShowForecastModal(false);
        alert(`Previsão aplicada com sucesso! ${changesToSave.length} metas atualizadas.`);
      }
    } catch (err) {
      console.error('Error applying forecast:', err);
      alert('Erro ao aplicar previsão');
    }
  };

  if (loading) {
    return (
      <div className="goals-list-page">
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Carregando...</span>
          </div>
          <p className="mt-2">Carregando matriz de targets...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="goals-list-page">
        <div className="alert alert-danger">{error}</div>
        <Link to={`/goals/${id}`} className="btn btn-secondary">Voltar</Link>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="goals-list-page">
        <div className="alert alert-warning">Nenhum dado encontrado</div>
        <Link to={`/goals/${id}`} className="btn btn-secondary">Voltar</Link>
      </div>
    );
  }

  const { goal, years, current_year, matrix } = data;

  return (
    <div className="goals-list-page">
      <div className="container-fluid py-4">
        {/* Header */}
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div>
            <h1 className="h4 mb-2 fw-bold">
              <i className="fas fa-bullseye me-2 text-primary"></i>
              Matriz de Targets - {goal.title}
            </h1>
            <div className="d-flex flex-wrap gap-3 align-items-center">
              <span className="badge bg-primary bg-opacity-10 text-primary px-3 py-2">
                <i className="fas fa-building me-1"></i>
                {goal.operation_name}
              </span>
              <span className="badge bg-secondary bg-opacity-10 text-secondary px-3 py-2">
                <i className="fas fa-map-marker-alt me-1"></i>
                {goal.unit_name}
              </span>
              <span className="badge bg-info bg-opacity-10 text-info px-3 py-2">
                <i className="fas fa-calendar-alt me-1"></i>
                {goal.period === 'monthly' ? 'Mensal' : 
                 goal.period === 'weekly' ? 'Semanal' : 
                 goal.period === 'daily' ? 'Diário' : 
                 goal.period === 'quarterly' ? 'Trimestral' : 
                 goal.period === 'annual' ? 'Anual' : goal.period}
              </span>
              <span className="badge bg-success bg-opacity-10 text-success px-3 py-2">
                <i className="fas fa-ruler me-1"></i>
                {goal.unit}
              </span>
            </div>
          </div>
          <div className="d-flex gap-2">
            {hasChanges && (
              <button 
                className="btn btn-success"
                onClick={handleSaveAll}
              >
                <i className="fas fa-save me-1"></i>Salvar ({Object.keys(changes).length})
              </button>
            )}
            <button 
              className="btn btn-primary"
              onClick={openForecastModal}
            >
              <i className="fas fa-chart-line me-1"></i>Projetar Metas
            </button>
            <button 
              className="btn btn-info text-white"
              onClick={() => setShowSummaryModal(true)}
            >
              <i className="fas fa-table me-1"></i>Resumo
            </button>
            <Link to={`/goals/${id}`} className="btn btn-outline-secondary">
              <i className="fas fa-arrow-left me-1"></i>Voltar para Meta
            </Link>
          </div>
        </div>

        {/* Info Card */}
        <div className="card mb-4">
          <div className="card-body">
            <div className="row">
              <div className="col-md-3">
                <div className="text-muted small">Meta Base</div>
                <div className="fw-bold">{formatNumber(goal.target_value)} {goal.unit}</div>
              </div>
              <div className="col-md-3">
                <div className="text-muted small">Período</div>
                <div className="fw-bold text-capitalize">{goal.period}</div>
              </div>
              <div className="col-md-3">
                <div className="text-muted small">Operação</div>
                <div className="fw-bold">{goal.operation_name}</div>
              </div>
              <div className="col-md-3">
                <div className="text-muted small">Unidade</div>
                <div className="fw-bold">{goal.unit_name}</div>
              </div>
            </div>
          </div>
        </div>

        {/* Matrix Table */}
        <div className="card">
          <div className="card-header bg-primary text-white">
            <h5 className="mb-0">
              <i className="fas fa-table me-2"></i>
              Matriz de Planejamento por Período
            </h5>
          </div>
          <div className="card-body p-0">
            <div className="table-responsive" style={{ maxHeight: '70vh', overflow: 'auto' }}>
              <table className="table table-bordered table-hover mb-0">
                <thead className="table-dark" style={{ position: 'sticky', top: 0, zIndex: 10 }}>
                  <tr>
                    <th style={{ position: 'sticky', left: 0, background: '#212529', zIndex: 11, minWidth: '150px' }}>
                      Período
                    </th>
                    {years.map(year => (
                      <th 
                        key={year} 
                        className={`text-center ${year === current_year ? 'bg-primary' : ''}`}
                        style={{ minWidth: '140px' }}
                      >
                        {year}
                        {year === current_year && <span className="badge bg-light text-dark ms-1">Atual</span>}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {matrix.map((row, rowIdx) => {
                    const monthNum = rowIdx + 1;
                    
                    // Função para calcular resumo
                    const calcSummaryInline = (year, startMonth, endMonth) => {
                      let totalTarget = 0;
                      let totalResult = 0;
                      let countResults = 0;
                      
                      matrix.forEach((r, idx) => {
                        const m = idx + 1;
                        if (m >= startMonth && m <= endMonth) {
                          const c = r.cells.find(cl => cl.year === year);
                          if (c) {
                            const tv = c.target ?? c.goal_target ?? null;
                            if (tv !== null && tv !== undefined) totalTarget += tv;
                            if (c.result !== null && c.result !== undefined && c.status === 'concluded') {
                              totalResult += c.result;
                              countResults++;
                            }
                          }
                        }
                      });
                      
                      const percentage = totalTarget > 0 ? (totalResult / totalTarget) * 100 : 0;
                      return { totalTarget, totalResult, percentage, countResults };
                    };
                    
                    const fmtN = (num) => num.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
                    const pctCls = (pct) => pct >= 100 ? 'text-success' : pct >= 80 ? 'text-warning' : 'text-danger';
                    
                    // Determinar quais resumos mostrar após este mês
                    const showQ1 = showSummaryInMatrix && summaryConfig.showQuarterly && monthNum === 3;
                    const showQ2 = showSummaryInMatrix && summaryConfig.showQuarterly && monthNum === 6;
                    const showQ3 = showSummaryInMatrix && summaryConfig.showQuarterly && monthNum === 9;
                    const showQ4 = showSummaryInMatrix && summaryConfig.showQuarterly && monthNum === 12;
                    const showS1 = showSummaryInMatrix && summaryConfig.showSemesterly && monthNum === 6;
                    const showS2 = showSummaryInMatrix && summaryConfig.showSemesterly && monthNum === 12;
                    const showYear = showSummaryInMatrix && summaryConfig.showAnnually && monthNum === 12;
                    
                    return (
                      <React.Fragment key={rowIdx}>
                        <tr>
                          <td style={{ position: 'sticky', left: 0, background: '#fff', zIndex: 5, fontWeight: 600 }}>
                            {row.label}
                          </td>
                          {row.cells.map((cell, cellIdx) => {
                        const cellKey = cell.date;
                        const targetEditKey = `${cell.date}_target`;
                        const resultEditKey = `${cell.date}_result`;
                        const hasChange = changes[cellKey];
                        const isSaving = savingCells[cellKey];
                        const isSaved = savedCells[cellKey];
                        
                        // Valores de exibição
                        const isEditingTarget = editingValues[targetEditKey] !== undefined;
                        const isEditingResult = editingValues[resultEditKey] !== undefined;
                        
                        const targetValue = isEditingTarget
                          ? editingValues[targetEditKey]
                          : (hasChange?.target !== undefined 
                              ? formatNumber(hasChange.target)
                              : formatNumber(cell.target || cell.goal_target));
                        
                        const resultValue = isEditingResult
                          ? editingValues[resultEditKey]
                          : (hasChange?.result !== undefined
                              ? formatNumber(hasChange.result)
                              : formatNumber(cell.result));
                        
                        // Determinar estilo da célula
                        const isCurrentYear = cell.year === current_year;
                        let cellClass = '';
                        if (isSaved) {
                          cellClass = '';
                        } else if (cell.result !== null && cell.result !== undefined) {
                          cellClass = 'table-success';
                        } else if (cell.target || cell.goal_target) {
                          cellClass = 'table-warning';
                        }
                        
                        return (
                          <td 
                            key={cellIdx}
                            className={cellClass}
                            style={{ 
                              textAlign: 'center', 
                              padding: '0.25rem',
                              backgroundColor: isSaved ? 'rgba(16, 185, 129, 0.15)' : 
                                              isSaving ? 'rgba(251, 191, 36, 0.15)' :
                                              isCurrentYear && !cellClass ? 'rgba(13, 110, 253, 0.05)' : undefined,
                              transition: 'background-color 0.3s ease'
                            }}
                          >
                            {(isSaving || isSaved) && (
                              <div style={{ fontSize: '0.6rem', color: isSaved ? '#10b981' : '#fbbf24', fontWeight: 'bold' }}>
                                {isSaving ? '⏳' : '✓'}
                              </div>
                            )}
                            
                            {/* Campo Meta */}
                            <div className="mb-1">
                              <input
                                type="text"
                                className="form-control form-control-sm"
                                value={targetValue}
                                onChange={(e) => {
                                  handleInputChange(cell.date, 'target', e.target.value);
                                  handleTargetChange(cell.date, 'target', e.target.value);
                                }}
                                onBlur={() => {
                                  if (editingValues[targetEditKey] !== undefined) {
                                    setEditingValues(prev => {
                                      const newState = { ...prev };
                                      delete newState[targetEditKey];
                                      return newState;
                                    });
                                  }
                                }}
                                placeholder="Meta"
                                disabled={isSaving}
                                style={{ 
                                  textAlign: 'center',
                                  fontSize: '0.75rem',
                                  padding: '0.2rem 0.4rem',
                                  border: isSaved ? '2px solid #10b981' : 
                                          isSaving ? '2px solid #fbbf24' :
                                          hasChange?.target !== undefined ? '2px solid #c5a572' : 
                                          '1px solid #dee2e6'
                                }}
                              />
                            </div>
                            
                            {/* Campo Resultado */}
                            <div>
                              <input
                                type="text"
                                className="form-control form-control-sm"
                                value={resultValue}
                                onChange={(e) => {
                                  handleInputChange(cell.date, 'result', e.target.value);
                                  handleTargetChange(cell.date, 'result', e.target.value, cell.goal_id);
                                }}
                                onBlur={() => {
                                  if (editingValues[resultEditKey] !== undefined) {
                                    setEditingValues(prev => {
                                      const newState = { ...prev };
                                      delete newState[resultEditKey];
                                      return newState;
                                    });
                                  }
                                }}
                                placeholder="Real"
                                disabled={isSaving}
                                style={{ 
                                  textAlign: 'center',
                                  fontSize: '0.75rem',
                                  padding: '0.2rem 0.4rem',
                                  backgroundColor: '#f8f9fa',
                                  border: isSaved ? '2px solid #10b981' : 
                                          isSaving ? '2px solid #fbbf24' :
                                          hasChange?.result !== undefined ? '2px solid #10b981' : 
                                          '1px solid #dee2e6'
                                }}
                              />
                            </div>
                            
                            {/* Percentual */}
                            {cell.percentage !== null && cell.percentage !== undefined && (
                              <div className="mt-1">
                                <small className={`fw-bold ${
                                  cell.percentage >= 100 ? 'text-success' :
                                  cell.percentage >= 80 ? 'text-warning' :
                                  'text-danger'
                                }`}>
                                  {cell.percentage}%
                                </small>
                              </div>
                            )}
                          </td>
                        );
                      })}
                        </tr>
                        
                        {/* Resumo 1º Trimestre - após março */}
                        {showQ1 && (
                          <tr className="table-primary">
                            <td style={{ position: 'sticky', left: 0, background: '#cfe2ff', zIndex: 5, fontWeight: 600 }}>
                              <small><i className="fas fa-chart-pie me-1"></i>1º Tri</small>
                            </td>
                            {years.map(year => {
                              const s = calcSummaryInline(year, 1, 3);
                              return (
                                <td key={year} className="text-center" style={{ fontSize: '0.75rem', padding: '0.25rem' }}>
                                  <div><strong>M:</strong> {fmtN(s.totalTarget)}</div>
                                  <div><strong>R:</strong> {s.countResults > 0 ? fmtN(s.totalResult) : '-'}</div>
                                  {s.countResults > 0 && <div className={`fw-bold ${pctCls(s.percentage)}`}>{s.percentage.toFixed(1)}%</div>}
                                </td>
                              );
                            })}
                          </tr>
                        )}
                        
                        {/* Resumo 2º Trimestre e 1º Semestre - após junho */}
                        {showQ2 && (
                          <tr className="table-primary">
                            <td style={{ position: 'sticky', left: 0, background: '#cfe2ff', zIndex: 5, fontWeight: 600 }}>
                              <small><i className="fas fa-chart-pie me-1"></i>2º Tri</small>
                            </td>
                            {years.map(year => {
                              const s = calcSummaryInline(year, 4, 6);
                              return (
                                <td key={year} className="text-center" style={{ fontSize: '0.75rem', padding: '0.25rem' }}>
                                  <div><strong>M:</strong> {fmtN(s.totalTarget)}</div>
                                  <div><strong>R:</strong> {s.countResults > 0 ? fmtN(s.totalResult) : '-'}</div>
                                  {s.countResults > 0 && <div className={`fw-bold ${pctCls(s.percentage)}`}>{s.percentage.toFixed(1)}%</div>}
                                </td>
                              );
                            })}
                          </tr>
                        )}
                        {showS1 && (
                          <tr className="table-success">
                            <td style={{ position: 'sticky', left: 0, background: '#d1e7dd', zIndex: 5, fontWeight: 600 }}>
                              <small><i className="fas fa-calendar-alt me-1"></i>1º Sem</small>
                            </td>
                            {years.map(year => {
                              const s = calcSummaryInline(year, 1, 6);
                              return (
                                <td key={year} className="text-center" style={{ fontSize: '0.75rem', padding: '0.25rem' }}>
                                  <div><strong>M:</strong> {fmtN(s.totalTarget)}</div>
                                  <div><strong>R:</strong> {s.countResults > 0 ? fmtN(s.totalResult) : '-'}</div>
                                  {s.countResults > 0 && <div className={`fw-bold ${pctCls(s.percentage)}`}>{s.percentage.toFixed(1)}%</div>}
                                </td>
                              );
                            })}
                          </tr>
                        )}
                        
                        {/* Resumo 3º Trimestre - após setembro */}
                        {showQ3 && (
                          <tr className="table-primary">
                            <td style={{ position: 'sticky', left: 0, background: '#cfe2ff', zIndex: 5, fontWeight: 600 }}>
                              <small><i className="fas fa-chart-pie me-1"></i>3º Tri</small>
                            </td>
                            {years.map(year => {
                              const s = calcSummaryInline(year, 7, 9);
                              return (
                                <td key={year} className="text-center" style={{ fontSize: '0.75rem', padding: '0.25rem' }}>
                                  <div><strong>M:</strong> {fmtN(s.totalTarget)}</div>
                                  <div><strong>R:</strong> {s.countResults > 0 ? fmtN(s.totalResult) : '-'}</div>
                                  {s.countResults > 0 && <div className={`fw-bold ${pctCls(s.percentage)}`}>{s.percentage.toFixed(1)}%</div>}
                                </td>
                              );
                            })}
                          </tr>
                        )}
                        
                        {/* Resumo 4º Trimestre, 2º Semestre e Anual - após dezembro */}
                        {showQ4 && (
                          <tr className="table-primary">
                            <td style={{ position: 'sticky', left: 0, background: '#cfe2ff', zIndex: 5, fontWeight: 600 }}>
                              <small><i className="fas fa-chart-pie me-1"></i>4º Tri</small>
                            </td>
                            {years.map(year => {
                              const s = calcSummaryInline(year, 10, 12);
                              return (
                                <td key={year} className="text-center" style={{ fontSize: '0.75rem', padding: '0.25rem' }}>
                                  <div><strong>M:</strong> {fmtN(s.totalTarget)}</div>
                                  <div><strong>R:</strong> {s.countResults > 0 ? fmtN(s.totalResult) : '-'}</div>
                                  {s.countResults > 0 && <div className={`fw-bold ${pctCls(s.percentage)}`}>{s.percentage.toFixed(1)}%</div>}
                                </td>
                              );
                            })}
                          </tr>
                        )}
                        {showS2 && (
                          <tr className="table-success">
                            <td style={{ position: 'sticky', left: 0, background: '#d1e7dd', zIndex: 5, fontWeight: 600 }}>
                              <small><i className="fas fa-calendar-alt me-1"></i>2º Sem</small>
                            </td>
                            {years.map(year => {
                              const s = calcSummaryInline(year, 7, 12);
                              return (
                                <td key={year} className="text-center" style={{ fontSize: '0.75rem', padding: '0.25rem' }}>
                                  <div><strong>M:</strong> {fmtN(s.totalTarget)}</div>
                                  <div><strong>R:</strong> {s.countResults > 0 ? fmtN(s.totalResult) : '-'}</div>
                                  {s.countResults > 0 && <div className={`fw-bold ${pctCls(s.percentage)}`}>{s.percentage.toFixed(1)}%</div>}
                                </td>
                              );
                            })}
                          </tr>
                        )}
                        {showYear && (
                          <tr className="table-warning">
                            <td style={{ position: 'sticky', left: 0, background: '#fff3cd', zIndex: 5, fontWeight: 600 }}>
                              <small><i className="fas fa-calendar-check me-1"></i>Anual</small>
                            </td>
                            {years.map(year => {
                              const s = calcSummaryInline(year, 1, 12);
                              return (
                                <td key={year} className="text-center" style={{ fontSize: '0.75rem', padding: '0.25rem' }}>
                                  <div><strong>M:</strong> {fmtN(s.totalTarget)}</div>
                                  <div><strong>R:</strong> {s.countResults > 0 ? fmtN(s.totalResult) : '-'}</div>
                                  {s.countResults > 0 && <div className={`fw-bold ${pctCls(s.percentage)}`}>{s.percentage.toFixed(1)}%</div>}
                                </td>
                              );
                            })}
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Legend */}
        <div className="card mt-3">
          <div className="card-body py-2">
            <div className="d-flex gap-4 align-items-center">
              <span className="text-muted small">Legenda:</span>
              <span><span className="badge bg-success me-1">&nbsp;</span> Com resultado</span>
              <span><span className="badge bg-warning me-1">&nbsp;</span> Apenas meta definida</span>
              <span><span className="badge" style={{ backgroundColor: 'rgba(13, 110, 253, 0.2)' }}>&nbsp;</span> Ano atual</span>
            </div>
          </div>
        </div>

        {/* Modal de Projeção */}
        {showForecastModal && (
          <div 
            className="modal show d-block" 
            tabIndex="-1" 
            style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
            onClick={(e) => e.target === e.currentTarget && setShowForecastModal(false)}
          >
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header bg-primary text-white">
                  <h5 className="modal-title">
                    <i className="fas fa-chart-line me-2"></i>
                    Projetar Metas por Crescimento
                  </h5>
                  <button 
                    type="button" 
                    className="btn-close btn-close-white" 
                    onClick={() => setShowForecastModal(false)}
                  ></button>
                </div>
                <div className="modal-body">
                  {/* Anos Base (informativo) */}
                  <div className="mb-3">
                    <label className="form-label fw-bold">
                      <i className="fas fa-calendar-alt me-1 text-secondary"></i>
                      Anos Base (referência com resultados)
                    </label>
                    <div className="p-2 bg-light border rounded">
                      {getYearsWithResults().length > 0 ? (
                        <div className="d-flex flex-wrap gap-2">
                          {getYearsWithResults().map(year => (
                            <span key={year} className="badge bg-success">
                              <i className="fas fa-check me-1"></i>{year}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <span className="text-muted">Nenhum ano com resultados concluídos</span>
                      )}
                    </div>
                    <small className="text-muted">Todos os anos com resultados serão usados como base para o cálculo</small>
                  </div>

                  {/* Ano Destino */}
                  <div className="mb-3">
                    <label className="form-label fw-bold">
                      <i className="fas fa-bullseye me-1 text-primary"></i>
                      Ano Destino (a preencher)
                    </label>
                    <select 
                      className="form-select"
                      value={forecastConfig.targetYear || ''}
                      onChange={(e) => setForecastConfig(prev => ({ ...prev, targetYear: parseInt(e.target.value) }))}
                    >
                      {years.filter(y => !getYearsWithResults().includes(y)).map(year => (
                        <option key={year} value={year}>{year}</option>
                      ))}
                    </select>
                    <small className="text-muted">Ano onde as metas serão preenchidas automaticamente</small>
                  </div>

                  {/* Tipo de Crescimento */}
                  <div className="mb-3">
                    <label className="form-label fw-bold">
                      <i className="fas fa-chart-bar me-1 text-info"></i>
                      Tipo de Crescimento
                    </label>
                    <div className="d-flex flex-column gap-2">
                      <div 
                        className={`p-3 border rounded ${forecastConfig.growthType === 'continuous' ? 'border-primary bg-primary bg-opacity-10' : 'bg-white'}`}
                        onClick={() => setForecastConfig(prev => ({ ...prev, growthType: 'continuous' }))}
                        style={{ cursor: 'pointer' }}
                      >
                        <div className="d-flex align-items-center">
                          <input 
                            type="radio" 
                            className="form-check-input me-2" 
                            checked={forecastConfig.growthType === 'continuous'}
                            onChange={() => setForecastConfig(prev => ({ ...prev, growthType: 'continuous' }))}
                          />
                          <div>
                            <strong className="text-dark">Contínuo</strong>
                            <small className="d-block text-secondary">
                              Crescimento uniforme mês a mês, partindo do último resultado do ano base
                            </small>
                          </div>
                        </div>
                      </div>
                      
                      <div 
                        className={`p-3 border rounded ${forecastConfig.growthType === 'seasonal' ? 'border-primary bg-primary bg-opacity-10' : 'bg-white'}`}
                        onClick={() => setForecastConfig(prev => ({ ...prev, growthType: 'seasonal' }))}
                        style={{ cursor: 'pointer' }}
                      >
                        <div className="d-flex align-items-center">
                          <input 
                            type="radio" 
                            className="form-check-input me-2" 
                            checked={forecastConfig.growthType === 'seasonal'}
                            onChange={() => setForecastConfig(prev => ({ ...prev, growthType: 'seasonal' }))}
                          />
                          <div>
                            <strong className="text-dark">Sazonal</strong>
                            <small className="d-block text-secondary">
                              Baseado no mesmo mês do ano anterior (mantém padrão sazonal)
                            </small>
                          </div>
                        </div>
                      </div>
                      
                      <div 
                        className={`p-3 border rounded ${forecastConfig.growthType === 'both' ? 'border-primary bg-primary bg-opacity-10' : 'bg-white'}`}
                        onClick={() => setForecastConfig(prev => ({ ...prev, growthType: 'both' }))}
                        style={{ cursor: 'pointer' }}
                      >
                        <div className="d-flex align-items-center">
                          <input 
                            type="radio" 
                            className="form-check-input me-2" 
                            checked={forecastConfig.growthType === 'both'}
                            onChange={() => setForecastConfig(prev => ({ ...prev, growthType: 'both' }))}
                          />
                          <div>
                            <strong className="text-dark">Misto</strong>
                            <small className="d-block text-secondary">
                              Combina crescimento contínuo + sazonalidade (50% cada)
                            </small>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Tipo de Valor: Percentual ou Absoluto */}
                  <div className="mb-3">
                    <label className="form-label fw-bold">
                      <i className="fas fa-calculator me-1 text-success"></i>
                      Crescimento/Redução
                    </label>
                    
                    {/* Toggle entre Percentual e Absoluto */}
                    <div className="btn-group w-100 mb-2" role="group">
                      <button
                        type="button"
                        className={`btn ${forecastConfig.growthMode === 'percent' ? 'btn-primary' : 'btn-outline-secondary'}`}
                        onClick={() => setForecastConfig(prev => ({ ...prev, growthMode: 'percent' }))}
                      >
                        <i className="fas fa-percent me-1"></i> Percentual
                      </button>
                      <button
                        type="button"
                        className={`btn ${forecastConfig.growthMode === 'absolute' ? 'btn-primary' : 'btn-outline-secondary'}`}
                        onClick={() => setForecastConfig(prev => ({ ...prev, growthMode: 'absolute' }))}
                      >
                        <i className="fas fa-hashtag me-1"></i> Valor Absoluto
                      </button>
                    </div>
                    
                    {/* Input conforme o modo */}
                    {forecastConfig.growthMode === 'percent' ? (
                      <>
                        <div className="input-group">
                          <input 
                            type="number" 
                            className="form-control"
                            value={forecastConfig.growthPercent}
                            onChange={(e) => setForecastConfig(prev => ({ ...prev, growthPercent: parseFloat(e.target.value) || 0 }))}
                            step="0.5"
                          />
                          <span className="input-group-text">%</span>
                        </div>
                        <small className="text-secondary">
                          Valores positivos = crescimento, negativos = redução (ex: -10 para reduzir 10%)
                        </small>
                      </>
                    ) : (
                      <>
                        <div className="input-group">
                          <input 
                            type="number" 
                            className="form-control"
                            value={forecastConfig.growthAbsolute}
                            onChange={(e) => setForecastConfig(prev => ({ ...prev, growthAbsolute: parseFloat(e.target.value) || 0 }))}
                            step="1"
                          />
                          <span className="input-group-text">total anual</span>
                        </div>
                        <small className="text-secondary">
                          Total anual que deseja alcançar (ex: 10800 para atingir 10.800 no ano)
                        </small>
                      </>
                    )}
                  </div>

                  {/* Limites de Valor */}
                  <div className="mb-3">
                    <label className="form-label fw-bold">
                      <i className="fas fa-sliders-h me-1 text-warning"></i>
                      Limites de Valor
                    </label>
                    <div className="row g-2">
                      <div className="col-6">
                        <div className="form-check mb-2">
                          <input 
                            type="checkbox" 
                            className="form-check-input" 
                            id="hasMinValue"
                            checked={forecastConfig.hasMinValue}
                            onChange={(e) => setForecastConfig(prev => ({ ...prev, hasMinValue: e.target.checked }))}
                          />
                          <label className="form-check-label text-dark" htmlFor="hasMinValue">
                            Valor mínimo (piso)
                          </label>
                        </div>
                        <input 
                          type="number" 
                          className="form-control form-control-sm"
                          value={forecastConfig.minValue}
                          onChange={(e) => setForecastConfig(prev => ({ ...prev, minValue: parseFloat(e.target.value) || 0 }))}
                          disabled={!forecastConfig.hasMinValue}
                          placeholder="Ex: 0"
                        />
                      </div>
                      <div className="col-6">
                        <div className="form-check mb-2">
                          <input 
                            type="checkbox" 
                            className="form-check-input" 
                            id="hasMaxValue"
                            checked={forecastConfig.hasMaxValue}
                            onChange={(e) => setForecastConfig(prev => ({ ...prev, hasMaxValue: e.target.checked }))}
                          />
                          <label className="form-check-label text-dark" htmlFor="hasMaxValue">
                            Valor máximo (teto)
                          </label>
                        </div>
                        <input 
                          type="number" 
                          className="form-control form-control-sm"
                          value={forecastConfig.maxValue}
                          onChange={(e) => setForecastConfig(prev => ({ ...prev, maxValue: parseFloat(e.target.value) || 100 }))}
                          disabled={!forecastConfig.hasMaxValue}
                          placeholder="Ex: 100"
                        />
                      </div>
                    </div>
                    <small className="text-secondary">
                      Defina limites para metas com valores mínimos (ex: 0) ou máximos (ex: 100%)
                    </small>
                  </div>

                  {/* Preview do cálculo */}
                  <div className="mb-3">
                    {(() => {
                      const yearsWithResults = getYearsWithResults();
                      if (yearsWithResults.length === 0 || !matrix) return null;
                      
                      // Contar meses com dados por ano e calcular totais
                      let totalBase = 0;
                      let totalMonthsWithData = 0;
                      const monthsPerYear = {};
                      
                      yearsWithResults.forEach(year => {
                        monthsPerYear[year] = 0;
                      });
                      
                      matrix.forEach(row => {
                        yearsWithResults.forEach(year => {
                          const cell = row.cells.find(c => c.year === year);
                          if (cell && cell.result !== null && cell.result !== undefined && cell.status === 'concluded') {
                            totalBase += cell.result;
                            totalMonthsWithData++;
                            monthsPerYear[year]++;
                          }
                        });
                      });
                      
                      // Média mensal real (baseada nos meses que existem)
                      const avgMonthBase = totalMonthsWithData > 0 ? totalBase / totalMonthsWithData : 0;
                      
                      // Extrapolar para 12 meses (ano completo)
                      const avgYearTotal = avgMonthBase * 12;
                      const targetTotal = forecastConfig.growthMode === 'absolute'
                        ? forecastConfig.growthAbsolute  // Valor alvo definido pelo usuário
                        : avgYearTotal * (1 + forecastConfig.growthPercent / 100);
                      const avgMonthTarget = targetTotal / 12;
                      
                      // Calcular variação para exibição
                      const variation = targetTotal - avgYearTotal;
                      const variationPercent = avgYearTotal > 0 ? ((targetTotal / avgYearTotal) - 1) * 100 : 0;
                      
                      // Verificar se há meses faltando
                      const avgMonthsPerYear = totalMonthsWithData / yearsWithResults.length;
                      const hasIncompleteData = avgMonthsPerYear < 12;
                      
                      return (
                        <div className="mt-2 p-2 bg-white border rounded shadow-sm">
                          <small className="d-block text-dark mb-1">
                            <strong>Prévia do cálculo:</strong>
                            {hasIncompleteData && (
                              <span className="badge bg-warning text-dark ms-2" style={{ fontSize: '0.65rem' }}>
                                <i className="fas fa-info-circle me-1"></i>
                                Extrapolado de {Math.round(avgMonthsPerYear)} meses
                              </span>
                            )}
                          </small>
                          <div className="row">
                            <div className="col-6">
                              <small className="d-block text-dark">
                                <i className="fas fa-calendar-alt me-1 text-secondary"></i>Total anual base{hasIncompleteData ? '*' : ''}:
                              </small>
                              <small className="d-block">
                                <strong className="text-primary">{avgYearTotal.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong>
                              </small>
                            </div>
                            <div className="col-6">
                              <small className="d-block text-dark">
                                <i className="fas fa-chart-line me-1 text-secondary"></i>Média mensal base:
                              </small>
                              <small className="d-block">
                                <strong className="text-primary">{avgMonthBase.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong>
                              </small>
                            </div>
                          </div>
                          <hr className="my-2" />
                          <div className="row">
                            <div className="col-6">
                              <small className="d-block text-dark">
                                <i className="fas fa-bullseye me-1 text-secondary"></i>Total destino ({forecastConfig.targetYear}):
                              </small>
                              <small className="d-block">
                                <strong className={variation >= 0 ? 'text-success' : 'text-danger'}>
                                  {targetTotal.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                </strong>
                                <span className={`badge ms-1 ${variation >= 0 ? 'bg-success' : 'bg-danger'}`} style={{ fontSize: '0.65rem' }}>
                                  {variation >= 0 ? '+' : ''}{variationPercent.toFixed(1)}%
                                </span>
                              </small>
                            </div>
                            <div className="col-6">
                              <small className="d-block text-dark">
                                <i className="fas fa-chart-bar me-1 text-secondary"></i>Média mensal destino:
                              </small>
                              <small className="d-block">
                                <strong className={variation >= 0 ? 'text-success' : 'text-danger'}>
                                  {avgMonthTarget.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                </strong>
                              </small>
                            </div>
                          </div>
                          {hasIncompleteData && (
                            <small className="d-block text-secondary mt-1" style={{ fontSize: '0.7rem' }}>
                              * Total extrapolado para 12 meses baseado na média dos {Math.round(avgMonthsPerYear)} meses com dados
                            </small>
                          )}
                          {(forecastConfig.hasMinValue || forecastConfig.hasMaxValue) && (
                            <small className="d-block text-warning mt-1" style={{ fontSize: '0.7rem' }}>
                              <i className="fas fa-exclamation-triangle me-1"></i>
                              Limites aplicados: 
                              {forecastConfig.hasMinValue && ` mín: ${forecastConfig.minValue}`}
                              {forecastConfig.hasMinValue && forecastConfig.hasMaxValue && ' |'}
                              {forecastConfig.hasMaxValue && ` máx: ${forecastConfig.maxValue}`}
                            </small>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                </div>
                <div className="modal-footer">
                  <button 
                    type="button" 
                    className="btn btn-secondary" 
                    onClick={() => setShowForecastModal(false)}
                  >
                    Cancelar
                  </button>
                  <button 
                    type="button" 
                    className="btn btn-primary"
                    onClick={executeForecast}
                  >
                    <i className="fas fa-magic me-1"></i>
                    Aplicar Projeção
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Modal de Resumo/Sumarização */}
        {showSummaryModal && data && (
          <div 
            className="modal show d-block" 
            tabIndex="-1"
            style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
          >
            <div className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
              <div className="modal-content">
                <div className="modal-header bg-info text-white">
                  <h5 className="modal-title">
                    <i className="fas fa-table me-2"></i>
                    Resumo de Metas e Resultados
                  </h5>
                  <button 
                    type="button" 
                    className="btn-close btn-close-white" 
                    onClick={() => setShowSummaryModal(false)}
                  ></button>
                </div>
                <div className="modal-body">
                  {/* Checkboxes para escolher visualizações */}
                  <div className="mb-3 d-flex gap-3">
                    <div className="form-check">
                      <input 
                        type="checkbox" 
                        className="form-check-input" 
                        id="showQuarterly"
                        checked={summaryConfig.showQuarterly}
                        onChange={(e) => setSummaryConfig(prev => ({ ...prev, showQuarterly: e.target.checked }))}
                      />
                      <label className="form-check-label" htmlFor="showQuarterly">
                        <i className="fas fa-calendar-alt me-1 text-primary"></i>Trimestral
                      </label>
                    </div>
                    <div className="form-check">
                      <input 
                        type="checkbox" 
                        className="form-check-input" 
                        id="showSemesterly"
                        checked={summaryConfig.showSemesterly}
                        onChange={(e) => setSummaryConfig(prev => ({ ...prev, showSemesterly: e.target.checked }))}
                      />
                      <label className="form-check-label" htmlFor="showSemesterly">
                        <i className="fas fa-calendar me-1 text-success"></i>Semestral
                      </label>
                    </div>
                    <div className="form-check">
                      <input 
                        type="checkbox" 
                        className="form-check-input" 
                        id="showAnnually"
                        checked={summaryConfig.showAnnually}
                        onChange={(e) => setSummaryConfig(prev => ({ ...prev, showAnnually: e.target.checked }))}
                      />
                      <label className="form-check-label" htmlFor="showAnnually">
                        <i className="fas fa-calendar-check me-1 text-warning"></i>Anual
                      </label>
                    </div>
                  </div>

                  {/* Tabelas de Resumo */}
                  {(() => {
                    const { years, matrix } = data;
                    
                    // Função para calcular resumo de um período
                    const calculatePeriodSummary = (year, startMonth, endMonth) => {
                      let totalTarget = 0;
                      let totalResult = 0;
                      let countResults = 0;
                      let countTargets = 0;
                      
                      matrix.forEach((row, idx) => {
                        const monthNum = idx + 1;
                        if (monthNum >= startMonth && monthNum <= endMonth) {
                          const cell = row.cells.find(c => c.year === year);
                          if (cell) {
                            // Usar target ou goal_target como fallback
                            const targetValue = cell.target ?? cell.goal_target ?? null;
                            if (targetValue !== null && targetValue !== undefined) {
                              totalTarget += targetValue;
                              countTargets++;
                            }
                            if (cell.result !== null && cell.result !== undefined && cell.status === 'concluded') {
                              totalResult += cell.result;
                              countResults++;
                            }
                          }
                        }
                      });
                      
                      const avgResult = countResults > 0 ? totalResult / countResults : 0;
                      const percentage = totalTarget > 0 ? (totalResult / totalTarget) * 100 : 0;
                      
                      return { totalTarget, totalResult, avgResult, percentage, countResults, countTargets };
                    };
                    
                    // Definições de períodos
                    const quarters = [
                      { name: '1º Trimestre', start: 1, end: 3 },
                      { name: '2º Trimestre', start: 4, end: 6 },
                      { name: '3º Trimestre', start: 7, end: 9 },
                      { name: '4º Trimestre', start: 10, end: 12 }
                    ];
                    
                    const semesters = [
                      { name: '1º Semestre', start: 1, end: 6 },
                      { name: '2º Semestre', start: 7, end: 12 }
                    ];
                    
                    const formatNumber = (num) => num.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
                    const getPercentageClass = (pct) => {
                      if (pct >= 100) return 'text-success';
                      if (pct >= 80) return 'text-warning';
                      return 'text-danger';
                    };
                    
                    return (
                      <>
                        {/* Resumo Trimestral */}
                        {summaryConfig.showQuarterly && (
                          <div className="mb-4">
                            <h6 className="text-primary mb-3">
                              <i className="fas fa-calendar-alt me-2"></i>Resumo Trimestral
                            </h6>
                            <div className="table-responsive">
                              <table className="table table-bordered table-sm">
                                <thead className="table-primary">
                                  <tr>
                                    <th>Trimestre</th>
                                    {years.map(year => (
                                      <th key={year} colSpan="4" className="text-center">{year}</th>
                                    ))}
                                  </tr>
                                  <tr>
                                    <th></th>
                                    {years.map(year => (
                                      <React.Fragment key={year}>
                                        <th className="text-center small">Meta</th>
                                        <th className="text-center small">Real</th>
                                        <th className="text-center small">%</th>
                                        <th className="text-center small">Média</th>
                                      </React.Fragment>
                                    ))}
                                  </tr>
                                </thead>
                                <tbody>
                                  {quarters.map(q => (
                                    <tr key={q.name}>
                                      <td className="fw-bold">{q.name}</td>
                                      {years.map(year => {
                                        const summary = calculatePeriodSummary(year, q.start, q.end);
                                        return (
                                          <React.Fragment key={year}>
                                            <td className="text-center">{formatNumber(summary.totalTarget)}</td>
                                            <td className="text-center">{summary.countResults > 0 ? formatNumber(summary.totalResult) : '-'}</td>
                                            <td className={`text-center fw-bold ${getPercentageClass(summary.percentage)}`}>
                                              {summary.countResults > 0 ? `${summary.percentage.toFixed(1)}%` : '-'}
                                            </td>
                                            <td className="text-center text-muted">{summary.countResults > 0 ? formatNumber(summary.avgResult) : '-'}</td>
                                          </React.Fragment>
                                        );
                                      })}
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        )}

                        {/* Resumo Semestral */}
                        {summaryConfig.showSemesterly && (
                          <div className="mb-4">
                            <h6 className="text-success mb-3">
                              <i className="fas fa-calendar me-2"></i>Resumo Semestral
                            </h6>
                            <div className="table-responsive">
                              <table className="table table-bordered table-sm">
                                <thead className="table-success">
                                  <tr>
                                    <th>Semestre</th>
                                    {years.map(year => (
                                      <th key={year} colSpan="4" className="text-center">{year}</th>
                                    ))}
                                  </tr>
                                  <tr>
                                    <th></th>
                                    {years.map(year => (
                                      <React.Fragment key={year}>
                                        <th className="text-center small">Meta</th>
                                        <th className="text-center small">Real</th>
                                        <th className="text-center small">%</th>
                                        <th className="text-center small">Média</th>
                                      </React.Fragment>
                                    ))}
                                  </tr>
                                </thead>
                                <tbody>
                                  {semesters.map(s => (
                                    <tr key={s.name}>
                                      <td className="fw-bold">{s.name}</td>
                                      {years.map(year => {
                                        const summary = calculatePeriodSummary(year, s.start, s.end);
                                        return (
                                          <React.Fragment key={year}>
                                            <td className="text-center">{formatNumber(summary.totalTarget)}</td>
                                            <td className="text-center">{summary.countResults > 0 ? formatNumber(summary.totalResult) : '-'}</td>
                                            <td className={`text-center fw-bold ${getPercentageClass(summary.percentage)}`}>
                                              {summary.countResults > 0 ? `${summary.percentage.toFixed(1)}%` : '-'}
                                            </td>
                                            <td className="text-center text-muted">{summary.countResults > 0 ? formatNumber(summary.avgResult) : '-'}</td>
                                          </React.Fragment>
                                        );
                                      })}
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        )}

                        {/* Resumo Anual */}
                        {summaryConfig.showAnnually && (
                          <div className="mb-4">
                            <h6 className="text-warning mb-3">
                              <i className="fas fa-calendar-check me-2"></i>Resumo Anual
                            </h6>
                            <div className="table-responsive">
                              <table className="table table-bordered table-sm">
                                <thead className="table-warning">
                                  <tr>
                                    <th>Ano</th>
                                    <th className="text-center">Meta Total</th>
                                    <th className="text-center">Realizado Total</th>
                                    <th className="text-center">Atingimento</th>
                                    <th className="text-center">Média Mensal</th>
                                    <th className="text-center">Meses c/ Resultado</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {years.map(year => {
                                    const summary = calculatePeriodSummary(year, 1, 12);
                                    return (
                                      <tr key={year}>
                                        <td className="fw-bold">{year}</td>
                                        <td className="text-center">{formatNumber(summary.totalTarget)}</td>
                                        <td className="text-center">{summary.countResults > 0 ? formatNumber(summary.totalResult) : '-'}</td>
                                        <td className={`text-center fw-bold ${getPercentageClass(summary.percentage)}`}>
                                          {summary.countResults > 0 ? `${summary.percentage.toFixed(1)}%` : '-'}
                                        </td>
                                        <td className="text-center text-muted">{summary.countResults > 0 ? formatNumber(summary.avgResult) : '-'}</td>
                                        <td className="text-center">
                                          <span className={`badge ${summary.countResults === 12 ? 'bg-success' : summary.countResults > 0 ? 'bg-warning' : 'bg-secondary'}`}>
                                            {summary.countResults}/12
                                          </span>
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        )}
                      </>
                    );
                  })()}
                </div>
                <div className="modal-footer">
                  <button 
                    type="button" 
                    className="btn btn-secondary" 
                    onClick={() => setShowSummaryModal(false)}
                  >
                    Fechar
                  </button>
                  <button 
                    type="button" 
                    className={`btn ${showSummaryInMatrix ? 'btn-success' : 'btn-primary'}`}
                    onClick={() => {
                      setShowSummaryInMatrix(!showSummaryInMatrix);
                      setShowSummaryModal(false);
                    }}
                  >
                    <i className={`fas ${showSummaryInMatrix ? 'fa-eye-slash' : 'fa-eye'} me-1`}></i>
                    {showSummaryInMatrix ? 'Ocultar da Matriz' : 'Mostrar na Matriz'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default GoalTargets;
